/**
 * Coordination between the horizontal app bar (in the top bar) and the
 * sidebar's legacy app list (PROD-1403). They are siblings in every app's
 * shell, so they meet in this tiny module store instead of a context:
 *
 *   - The sidebar publishes the CURRENT app id (it already knows it from
 *     `AppSidebarAppSwitcher currentAppId`), so the bar can mark it active
 *     without every top bar repeating the id.
 *   - The bar publishes whether it is SHOWING. It shows only when every
 *     app icon fits across; otherwise (narrow window, phone) it hides and
 *     the sidebar falls back to today's expandable app list. Apps whose top
 *     bar never mounts the bar (Agent, Previews) keep the list too.
 *
 * Mirrors the subscribe/getSnapshot module stores in `./app-pins` and
 * `./hooks/use-ordo-apps`.
 */
import { useSyncExternalStore } from 'react'

interface AppBarState {
  currentAppId: string | undefined
  barShowing: boolean
}

let state: AppBarState = { currentAppId: undefined, barShowing: false }
const listeners = new Set<() => void>()

function set(next: Partial<AppBarState>): void {
  const merged = { ...state, ...next }
  if (merged.currentAppId === state.currentAppId && merged.barShowing === state.barShowing) {
    return
  }
  state = merged
  for (const l of listeners) {
    l()
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getSnapshot = (): AppBarState => state
const SERVER_STATE: AppBarState = { currentAppId: undefined, barShowing: false }
const getServerSnapshot = (): AppBarState => SERVER_STATE

export function setAppBarCurrentAppId(currentAppId: string | undefined): void {
  set({ currentAppId })
}

export function setAppBarShowing(barShowing: boolean): void {
  set({ barShowing })
}

export function useAppBarState(): AppBarState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
